/* eslint-disable no-case-declarations */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Tabs } from 'react-bootstrap';
import { map, get, find } from 'lodash';
import ndjsonStream from 'can-ndjson-stream';

import MovieInfo from '../MovieReport/MovieInfo';
import showsConfig from '../configs/shows-config';
import Iframe from './../../components/Iframe';
import SampleSize from '../../components/SampleSize';
import ShowInfo from './ShowInfo';
import { TabbedFilters, FilterTypes } from '../../components/Filters';
import PageExport from '../../components/PageExport';
import tvtApi from '../../services/tvt-api';
import lookerApi from '../../services/looker-api';
import { serializeFilters } from '../../utils/filters';
import { contentReportTabConversion } from '../../utils/shared';
import { TabbedFilterTooltip } from './table-config';
import {
  renderAnticipationTab,
  renderAudienceTab,
  renderEngagementTab,
  renderAffinityTab,
} from './Tabs';

import './styles/ShowReports.css';

import {
  formatShowViewersCount,
  getSampleSize,
  isAiredShow,
  getCurrentTabFromPath
} from '../../utils/show';

import {
  benchmarkTooltipDeactivated,
  showInitialState,
  showReducer
} from '../../utils/benchmark';

const ShowReport = props => {
  const [benchmarkData, dispatch] = useReducer(showReducer, showInitialState);
  const [benchmark, setBenchmark] = useState(null);
  const [displayBenchmarkTooltip, setDisplayBenchmarkTooltip] = useState(false);
  const [currentTab, setCurrentTab] = useState(
    getCurrentTabFromPath(props.history, showsConfig, props.showId)
  );
  const updatePath = newTab => {
    const paths = props.history.location.pathname.split('/');
    const { query } = props.history.location;
    const lastPath = paths[paths.length - 1];
    const tabName = contentReportTabConversion(newTab);

    if (lastPath !== props.showId) {
      paths[paths.length - 1] = tabName;
    } else {
      paths.push(tabName);
    }

    props.history.push({
      pathname: paths.join('/'),
      search: query
    });
  };

  const handleTabChange = nextTab => {
    setCurrentTab(nextTab);
  };

  const fetchBenchmarkData = async (
    filterValues = {},
    uuid,
    topData = false
  ) => {
    dispatch({ type: 'RESET', topData });
    const { benchmarks } = props;
    const fv = filterValues;
    let isCustomBenchmark = uuid && uuid !== 'similar-shows';
    if (isCustomBenchmark) {
      const bm = find(benchmarks, b => b.uuid === uuid);
      let urlBenchmarkFilters;
      if (bm) {
        setBenchmark(bm);
        urlBenchmarkFilters = JSON.parse(get(bm, 'filters', ''));
      } else {
        const seg = await tvtApi.getSegment(uuid);
        urlBenchmarkFilters = JSON.parse(get(seg, 'filters', ''));
        setBenchmark(seg);
      }
      urlBenchmarkFilters.forEach(f => {
        fv[f.id] = f;
      });
    } else if (!topData) {
      setBenchmark(null);
    }

    const hasCorrectShow =
      get(benchmarkData, ['showId'], null) === props.showId;

    const body = {
      filters: serializeFilters(fv),
      page: currentTab,
      showId: props.showId,
      tier: hasCorrectShow ? get(benchmarkData, ['tier', 'tier'], null) : null,
      topData,
      isCustomBenchmark
    };
    const apiRequestId = JSON.stringify(body);
    const dispatchObj = { type: 'INITIATE_REQUEST', topData };
    if (topData) {
      dispatchObj.apiRequestTop = apiRequestId;
    } else {
      dispatchObj.apiRequestBottom = apiRequestId;
    }
    dispatch(dispatchObj);
    try {
      const response = await lookerApi.createBenchmarkRequest(body);
      const reader = ndjsonStream(response.body).getReader();

      const read = r => {
        if (r.done) {
          dispatch({ type: 'LOADING_DONE', topData });
          return;
        }
        map(r.value, (v, k) => {
          dispatch({
            type: 'UPDATE',
            value: r.value,
            [topData ? 'apiRequestTop' : 'apiRequestBottom']: apiRequestId,
            showId: props.showId
          });
        });
        reader.read().then(read);
      };

      reader.read().then(result => {
        read(result);
      });
    } catch (err) {
      dispatch({ type: 'LOADING_DONE' });
      console.log(err);
    }
  };

  const updateFilters = () => {
    const {
      filtersActions: { getFiltersForPageAsync }
    } = props;

    getFiltersForPageAsync(currentTab, {
      showId: props.showId,
      syncWithUrl: true
    });
  };

  useEffect(() => {
    const {
      filtersActions: { resetFilters },
      fetchShow,
      match: {
        params: { showId }
      }
    } = props;

    setCurrentTab(
      getCurrentTabFromPath(props.history, showsConfig, props.showId)
    );
    resetFilters();
    updateFilters();
    fetchShow(showId);
    fetchBenchmarkData({}, props.selectedBenchmark);
  }, [props.showId]);

  useEffect(() => {
    updateFilters();
    updatePath(currentTab);
    if (!benchmarkData[`${currentTab}Top`] && currentTab  !== showsConfig.SHOW_AFFINITY_PAGE_ID ) {
      fetchBenchmarkData({}, props.selectedBenchmark);
    }
  }, [currentTab]);

  useEffect(() => {
    const shouldDisplay = benchmarkTooltipDeactivated();
    if (shouldDisplay === 'false') {
      setDisplayBenchmarkTooltip(false);
    } else {
      setTimeout(() => setDisplayBenchmarkTooltip(true), 2000);
    }
  }, [localStorage]);

  useEffect(() => {
    const { loadAvailableSeasons, show, filters } = props;
    if (
      show &&
      (currentTab === 'showReportViewership' ||
        currentTab === 'showReportEngagement') &&
      (!filters.options.season || !filters.options.season.length)
    ) {
      loadAvailableSeasons(show);
    }
  }, [props.filters]);

  useEffect(() => {
    if (props.show) {
      document.title = `${props.show.name} | Show | CVM Insights`;
    } else {
      document.title = 'Loading | CVM Insights';
    }

    return () => {
      document.title = document.URL;
    };
  }, [props.show]);

  const hasShowAired = isAiredShow(props.show);

  const renderShowInfo = () => {
    const {
      blocked,
      show,
      viewershipQueries,
      engagementQueries,
      anticipationQueries
    } = props;

    const isAudienceTab = currentTab === showsConfig.SHOW_VIEWERSHIP_PAGE_ID;
    const sampleSize = isAudienceTab
      ? getSampleSize(viewershipQueries.showSampleSize, blocked)
      : 0;
    const followers =
      viewershipQueries.showFollowers ||
      engagementQueries.showFollowers ||
      anticipationQueries.showFollowers;

    return (
      <header className="tvt-main__header">
        <Row className="tvt-add-flex">
          <Col md={10}>
            <ShowInfo show={show} count={formatShowViewersCount(followers)} />
          </Col>
          <Col md={2} className="tvt-column--bottom-element">
            <div style={{ position: 'relative' }}>
              {false && <PageExport title="Export all" pdf />}
            </div>
            {sampleSize > 0 && <SampleSize population={sampleSize} pinRight />}
          </Col>
        </Row>
      </header>
    );
  };

  const showEmptyState = () => {
    const isNoShow = !props.show;
    const isNotAired = !hasShowAired;
    const isTabCanShowNotReleasedShow = (
      currentTab === showsConfig.SHOW_ANTICIPATION_PAGE_ID
      || currentTab === showsConfig.SHOW_AFFINITY_PAGE_ID
    );
    return isNoShow || (isNotAired && !isTabCanShowNotReleasedShow);
  }

  return showEmptyState() ? (
    <Row className="tvt-add-flex">
      <Col md={10}>
        <MovieInfo movie={{}} loading />
      </Col>
    </Row>
  ) : (
    <>
      {renderShowInfo()}
      {props.blocked && (
        <Iframe
          blocked
          blockedMessage="Contact your Client Success Representative to learn more about this report"
          url=""
          height="100%"
        />
      )}

      {!props.blocked && (
        <Tabs
          activeKey={currentTab}
          className="tvt-show-report tvt-main__content-tabs"
          id="show-detail-tabs"
          onSelect={handleTabChange}
        >
          <TabbedFilters
            requiredSegmentValues={['show']}
            hideFilters={['show']}
            tooltip={TabbedFilterTooltip}
          />
          {renderAnticipationTab(
            props,
            currentTab,
            benchmarkData,
            fetchBenchmarkData,
            benchmark,
            displayBenchmarkTooltip,
            setDisplayBenchmarkTooltip
          )}
          {hasShowAired &&
            renderAudienceTab(
              props,
              currentTab,
              benchmarkData,
              fetchBenchmarkData,
              benchmark,
              displayBenchmarkTooltip,
              setDisplayBenchmarkTooltip
            )}
          {hasShowAired &&
            renderEngagementTab(
              props,
              currentTab,
              benchmarkData,
              fetchBenchmarkData,
              benchmark,
              displayBenchmarkTooltip,
              setDisplayBenchmarkTooltip
            )}
          {
            renderAffinityTab(
              props,
              currentTab,
              hasShowAired
            )
          }
        </Tabs>
      )}
    </>
  );
};

ShowReport.propTypes = {
  blocked: PropTypes.bool.isRequired,

  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.string
    }).isRequired
  }).isRequired,

  queryActions: PropTypes.shape({
    runInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  viewershipLooks: PropTypes.shape({
    showAffinity: PropTypes.shape({}),
    showAgeIndex: PropTypes.shape({}),
    showCountryIndex: PropTypes.shape({}),
    showGenderIndex: PropTypes.shape({}),
    showViewerDropOff: PropTypes.shape({})
  }).isRequired,

  viewershipQueries: PropTypes.shape({
    showFavorites: PropTypes.shape({}),
    showFollowers: PropTypes.shape({}),
    showMobility: PropTypes.shape({}),
    showSampleSize: PropTypes.shape({}),
    showScoreBinge: PropTypes.shape({}),
    showScoreCompletion: PropTypes.shape({}),
    showScoreEngagement: PropTypes.shape({}),
    showScoreViewshare: PropTypes.shape({})
  }).isRequired,

  engagementLooks: PropTypes.shape({
    engagementRawTotals: PropTypes.shape({}),
    showEpisodeEmotions: PropTypes.shape({})
  }).isRequired,

  engagementQueries: PropTypes.shape({
    characterVoteEpisodeRank: PropTypes.shape({}),
    hottestMoments: PropTypes.shape({}),
    memesList: PropTypes.shape({}),
    showFollowers: PropTypes.shape({}),
    showEmotionIndex: PropTypes.shape({}),
    showEmotionVotes: PropTypes.shape({})
  }).isRequired,

  anticipationLooks: PropTypes.shape({
    anticipationFollowerAffinity: PropTypes.shape({}),
    anticipationNewFollowers: PropTypes.shape({}),
    anticipationTotalFollowers: PropTypes.shape({})
  }).isRequired,

  anticipationQueries: PropTypes.shape({
    showFollowers: PropTypes.shape({})
  }).isRequired,

  fetchShow: PropTypes.func.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType, // eslint-disable-line react/no-typos
    options: PropTypes.shape({}),
    config: PropTypes.shape({})
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired,

  loadAvailableSeasons: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      showId: PropTypes.string
    })
  }).isRequired,
  show: PropTypes.shape({}),
  showId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

  selectedBenchmark: PropTypes.string,
  benchmarks: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

ShowReport.defaultProps = {
  show: null,
  selectedBenchmark: null
};

export default ShowReport;
